 
import axios from 'axios';

const api = axios.create({
  baseURL: (import.meta.env.VITE_API_URL as string | undefined) ?? '/api',
});

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface StoreInfo {
  id: string;
  name: string;
  deliveryEnabled: boolean;
  storePhone: string | null;
  storeStreet: string | null;
  storeNeighborhood: string | null;
  storeCity: string | null;
  storeState: string | null;
  logoUrl: string | null;
  storeLat: number | null;
  storeLng: number | null;
  deliveryZones: DeliveryZone[];
  modules: Record<string, unknown>;
  platformFeeCents: number;
}

export interface DeliveryZone {
  id: string;
  name: string;
  radiusKm: number;
  feeCents: number;
  estimatedMin: number;
}

export interface CatalogCategory {
  id: string;
  name: string;
  color: string | null;
  products: CatalogProduct[];
}

export interface DeliveryStoreConfig {
  store_name: string;
  min_order_cents: number;
  delivery_fee_cents: number;
  free_delivery_above_cents: number | null;
  estimated_time_min: number;
  estimated_time_max: number;
  accepts_delivery: boolean;
  accepts_pickup: boolean;
  announcement: string | null;
  announcement_active: boolean;
}

// Desconto progressivo por quantidade (bebidas avulsas -- latas/long neck).
// Vem preenchido pelo backend só pra produtos elegíveis (ver delivery.service.ts,
// calcularPrecoProgressivo); array vazio/ausente pra produtos fora do escopo.
export interface BulkPricingTier {
  qty: number;
  unitPriceCents: number;
  discountPct: number;
}

export interface CatalogProduct {
  id: string;
  name: string;
  description: string | null;
  deliveryDescription: string | null;
  imageUrl: string | null;
  salePriceCents: number;
  deliveryPriceCents: number;
  hasCustomDeliveryPrice: boolean;
  stock: number;
  isBestSeller: boolean;
  isPromo: boolean;
  isHighlight: boolean;
  // 13/07/2026 (a pedido do Thales: "oferecer a caixa fechada quando abre
  // o produto avulso"): presente só em produtos "filho" (caixa/engradado),
  // aponta pro id do produto avulso "pai". Se aparece na resposta pública,
  // já foi confirmado disponível pelo backend (getPublicCatalog só inclui
  // o filho se o pai tiver estoque suficiente) -- ver upsell.ts.
  parentProductId?: string | null;
  bulkPricing?: BulkPricingTier[];
}

// Dado um produto (com bulkPricing vindo do catálogo) e uma quantidade em
// unidades inteiras, devolve o preço unitário efetivo pra essa quantidade.
// Espelha a lógica de calcularPrecoProgressivo do backend, mas só lendo os
// degraus já calculados -- nunca recalcula preço no cliente.
export function getUnitPriceForQuantity(product: CatalogProduct, unitQty: number): number {
  // Preço "base" pra qty=1 é o mesmo já usado hoje na UI (deliveryPriceCents,
  // que cai pra salePriceCents quando não há preço customizado de delivery).
  const basePrice = product.deliveryPriceCents ?? product.salePriceCents;
  const tiers = product.bulkPricing;
  if (!tiers || tiers.length === 0 || unitQty <= 1) return basePrice;
  // Pega o maior degrau (qty) que seja <= unitQty (ex: 5 unidades usa o degrau de 4+)
  let best: BulkPricingTier | null = null;
  for (const tier of tiers) {
    if (unitQty >= tier.qty && (best === null || tier.qty > best.qty)) {
      best = tier;
    }
  }
  return best ? best.unitPriceCents : basePrice;
}

export type PaymentMethod = 'PIX' | 'CREDIT_CARD' | 'CASH_ON_DELIVERY' | 'DEBIT_ON_DELIVERY';

// (12/07/2026, revisão de segurança de frontend) -- existiam aqui os tipos
// CreditCardData (holderName/number/expiryMonth/expiryYear/ccv) e
// CardHolderInfo, com campos correspondentes em CreateOrderPayload. Removidos
// por serem código morto: nenhuma tela do delivery-app monta ou envia esses
// campos hoje (StorePage.tsx só usa PaymentMethod como rótulo -- "cartão" ali
// significa maquininha na entrega, não coleta de número de cartão no site).
// Não é risco em si (nunca chegou a ser usado), mas número de cartão/CVV cru
// não deveria viver como tipo pronto pra uso no bundle do frontend -- se um
// dia for implementado checkout com cartão online de verdade, isso deve ir
// direto pro SDK do gateway de pagamento (tokenização no client), nunca
// trafegar como JSON solto pro nosso próprio backend.
export interface CreateOrderPayload {
  customerName: string;
  customerPhone: string;
  customerCpf?: string;
  address: {
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    zipcode?: string;
    lat?: number;
    lng?: number;
    reference?: string;
  };
  // Zona de entrega escolhida pelo cliente no checkout (Centro/Rural/etc) --
  // 12/07/2026, a pedido do Thales: sem isso o backend nunca sabia qual
  // taxa de entrega cobrar (não tinha lat/lng nem zona), então todo pedido
  // saía com taxa R$0. Alternativa mais simples que geolocalização pra uma
  // cidade pequena com poucas zonas configuradas.
  zoneId?: string;
  items: { productId: string; quantity: number }[];
  promoCode?: string;
  // Código do link rastreável de indicação (?ref=CODE) -- 12/07/2026, a
  // pedido do Thales. Lido do localStorage (ver src/lib/referral.ts), nunca
  // bloqueia o pedido se for inválido/ausente.
  referralCode?: string;
  notes?: string;
  paymentMethod?: PaymentMethod;
}

export interface PixPaymentData {
  paymentId: string;
  pixQrCodeBase64: string;
  pixCopiaECola: string;
  expiresAt: string;
  value: number;
}

export interface OrderCreatedResponse {
  orderId: string;
  id?: string;
  status: string;
  totalCents: number;
  deliveryFeeCents: number;
  discountCents: number;
  estimatedMin: number;
  paymentMethod?: PaymentMethod;
  pix?: PixPaymentData | null;
  creditCard?: { paymentId: string; success: boolean } | null;
}

export interface OrderStatus {
  id: string;
  deliveryStatus: string;
  customerName: string;
  totalCents: number;
  deliveryFeeCents: number;
  deliveryZone: { estimatedMin: number } | null;
  items: { quantity: number; unitPriceCents: number; product: { name: string } }[];
}

export interface PromoValidation {
  valid: boolean;
  promoId: string;
  code: string;
  type: string;
  discountCents: number;
  freeDelivery: boolean;
  description: string | null;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export const deliveryApi = {
  getStoreInfo: (slug: string) =>
    api.get<StoreInfo>(`/delivery/public/${slug}`).then((r) => r.data),

  getCatalog: (slug: string) =>
    api.get<{ tenantId: string; categories: CatalogCategory[]; highlightProducts?: CatalogProduct[]; store: DeliveryStoreConfig | null; categoryAffinity?: Record<string, string[]>; storeOpen?: boolean; storeHoursMessage?: string | null }>(`/delivery/public/${slug}/catalog`).then((r) => r.data),

  createOrder: (slug: string, payload: CreateOrderPayload) =>
    api.post<OrderCreatedResponse>(`/delivery/public/${slug}/orders`, payload).then((r) => r.data),

  getOrderStatus: (slug: string, orderId: string) =>
    api.get<OrderStatus>(`/delivery/public/${slug}/orders/${orderId}`).then((r) => r.data),

  getPaymentStatus: (paymentId: string) =>
    api.get<{ status: string; value: number }>(`/delivery/public/payment/${paymentId}/status`).then((r) => r.data),

  validatePromo: (tenantSlug: string, code: string, orderSubtotalCents: number) =>
    api.post<PromoValidation>('/promo/validate', { tenantSlug, code, orderSubtotalCents }).then((r) => r.data),
};
